import React, { useMemo, useState } from "react";

import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";

import {
	ButtonStandard,
	Chip,
	DropDownMenu,
	type DropDownMenuOption,
	Icon,
	ProgressBar,
	Text,
} from "@performyard/py-components";

import { Divider } from "../../../../components/Divider";
import type { EmployeeValue } from "../../../../components/Inputs/SelectEmployee";
import { ActingManagerModal } from "./ActingManagerModal";

import type { GroupBudgetFigures } from "../MyDecisions/types";

import { formatCurrencyFull } from "../../utils";

export type BudgetSummaryCardPage = "my-decisions" | "budget-reviews";

export type BudgetSummaryCardProps = {
	figures: GroupBudgetFigures;
	onViewAuditLog: () => void;
	page: BudgetSummaryCardPage;
	budgetManagerForActingManagerPicker?: EmployeeValue;
	compensationCycleId?: string;
	budgetStatus?: string;
	assignedActingManager?: EmployeeValue;
	cycleStatus: string;
};

export const BudgetSummaryCard = ({
	figures,
	onViewAuditLog,
	page,
	budgetManagerForActingManagerPicker,
	compensationCycleId,
	budgetStatus = "",
	assignedActingManager,
	cycleStatus,
}: BudgetSummaryCardProps): React.ReactElement => {
	const isBudgetReviews = page === "budget-reviews";
	const { groupIncreaseBudget, groupAllocated, groupPercentAllocated, groupRemaining, isGroupOverBudget } = figures;
	const [actingManagerModalOpen, setActingManagerModalOpen] = useState(false);

	const showOverflowMenu =
		isBudgetReviews && budgetStatus === "pending" && (cycleStatus === "active" || cycleStatus === "scheduled");

	const overflowMenuOptions: DropDownMenuOption[] = useMemo(
		() => [
			{
				key: "assign-acting-manager",
				label: "Assign an Acting Manager",
				onClick: () => setActingManagerModalOpen(true),
			},
		],
		[]
	);

	const textSizeHeading = isBudgetReviews ? "paragraph-1" : "heading-5";
	const textSizeLabel = isBudgetReviews ? "caption" : "paragraph-3";
	const buttonSize = isBudgetReviews ? "small" : "medium";
	const buttonTextClass = isBudgetReviews ? "!text-caption" : "!text-paragraph-3";

	return (
		<div
			className={
				isBudgetReviews
					? "box-border flex h-[80px] flex-col gap-2 rounded-sm bg-white px-4 py-2"
					: "flex flex-col gap-4 rounded-sm bg-white p-4"
			}
			style={{ border: "1px solid var(--py-neutral-2)" }}
		>
			<div className="flex min-h-0 min-w-0 flex-1 flex-row items-center">
				<div className="flex min-w-0 shrink-0 items-stretch gap-8">
					<div className="flex flex-col pr-8">
						<Text size={textSizeLabel} color="neutral-7">
							Team Budget
						</Text>
						<Text size={textSizeHeading} weight="semi-bold">
							{formatCurrencyFull(groupIncreaseBudget)}
						</Text>
					</div>
					<Divider orientation="vertical" />
					<div className="flex flex-col pr-8">
						<Text size={textSizeLabel} color="neutral-7">
							Allocated
						</Text>
						<div className="flex items-center gap-2">
							<Text size={textSizeHeading} weight="semi-bold">
								{groupAllocated === 0 ? "$0" : formatCurrencyFull(groupAllocated)}
							</Text>
							<Chip
								label={`${groupPercentAllocated.toFixed(0)}%`}
								color={isGroupOverBudget ? "red" : "green"}
								size={20}
							></Chip>
						</div>
					</div>
					<Divider orientation="vertical" />
					<div className="flex flex-col">
						<Text size={textSizeLabel} color="neutral-7">
							Remaining
						</Text>
						<Text size={textSizeHeading} weight="semi-bold">
							{groupRemaining === 0 ? "$0" : formatCurrencyFull(groupRemaining)}
						</Text>
					</div>
				</div>
				<div className="ml-auto flex shrink-0 items-center gap-2">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size={buttonSize}
						dangerouslySetClassName={`!w-auto w-[105px] !border-neutral-3 !text-neutral-7 ${buttonTextClass}`}
						onClick={onViewAuditLog}
					>
						View Audit Log
					</ButtonStandard>
					{showOverflowMenu && (
						<DropDownMenu
							bordered
							placement="bottom-end"
							options={overflowMenuOptions}
							className="flex !h-6 !min-h-0 !w-6 shrink-0 !p-0"
							menuClassName="[&_p]:!text-caption [&_p]:!font-normal [&_[role=menuitem]]:!py-0.5 [&_[role=menuitem]]:!px-2"
							minWidth={150}
						>
							<Icon icon={faEllipsisVertical} size={20} color="neutral-7" />
						</DropDownMenu>
					)}
				</div>
			</div>
			<ProgressBar value={Math.min(100, groupPercentAllocated)} color={isGroupOverBudget ? "red" : "green"} />
			{isBudgetReviews && budgetManagerForActingManagerPicker && compensationCycleId && (
				<ActingManagerModal
					isOpen={actingManagerModalOpen}
					onClose={() => setActingManagerModalOpen(false)}
					budgetManager={budgetManagerForActingManagerPicker}
					compensationCycleId={compensationCycleId}
					assignedActingManager={assignedActingManager}
				/>
			)}
		</div>
	);
};
